import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import moment from 'moment';
import { DialFailureSide, DialLogRecord } from './dial-logs-timeline.model';
import { DIAL_LOGS_TOOLTIP_OPTIONS } from './dial-logs-timeline.tooltip';
import { DialLogsTimelineDrawerService } from './dial-logs-timeline-drawer.service';

/** One hop in the unified dialer → service → edge router → host connection trace. */
interface TraceRow {
    role: 'Dialer' | 'Service' | 'Edge Router' | 'Host';
    /** reached = traversed OK, failed = broke here, unreached = never got this far,
     *  idle = in the path but nothing was routed through it (no success, not the culprit). */
    state: 'reached' | 'failed' | 'unreached' | 'idle';
    /** copy-to-clipboard key. */
    key: string;
    /** Full, faithful entity name shown verbatim (or a status for transit hops). */
    name: string;
    /** Value for copy (the full name; empty when there is none). */
    raw: string;
    /** Router-link to the entity's detail page, or null when it has no navigable id. */
    link: unknown[] | null;
    /** The failed hop is the best guess at the culprit. */
    implicated: boolean;
    /** Short status/target sub-line (host row only). */
    sub: string | null;
    /** Fabric/router transit hop — no entity name, so head is a status, not a name. */
    isTransit: boolean;
    /** Transit hop: the collapsed ingress → egress routers, when the path is known. */
    routers?: { name: string; link: unknown[] | null }[];
    /** Transit hop meta, e.g. "2 routers · 1 link". */
    routerMeta?: string | null;
    /** Transit hop: the circuit crossed transit routers beyond ingress/egress. */
    hasMorePath?: boolean;
    /** Transit hop: every router in path order, with its ingress/transit/egress role. */
    fullPath?: { name: string; link: unknown[] | null; role: string }[];
}

/** Everything we can surface about one failed dial, opened from a failed child row. */
@Component({
    selector: 'app-dial-logs-failure-detail',
    templateUrl: './dial-logs-failure-detail.component.html',
    styleUrls: ['./dial-logs-failure-detail.component.scss'],
    providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: DIAL_LOGS_TOOLTIP_OPTIONS }],
    standalone: false,
})
export class DialLogsFailureDetailComponent {
    copiedKey: string | null = null;
    /** Whether the Edge Router hop's full ordered path (transit routers) is expanded. */
    showFullPath = false;
    private copyTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { record: DialLogRecord; routers?: Map<string, { name: string; id: string }> },
        private dialogRef: MatDialogRef<DialLogsFailureDetailComponent>,
        private drawerService: DialLogsTimelineDrawerService
    ) {}

    get record(): DialLogRecord {
        return this.data.record;
    }

    private get side(): DialFailureSide {
        return this.record.failureHint?.side ?? 'unknown';
    }

    get whenAbsolute(): string {
        return moment.utc(this.record.timestampMs).local().format('MMM D, YYYY · h:mm:ss a');
    }

    /** Plain-English, entity-free failure summary (identity lives in the trace). */
    get reasonSegments(): { text: string; mono?: boolean }[] {
        return this.record.failureHint?.headline ?? [{ text: 'Dial failed' }];
    }

    get explanation(): string {
        return this.record.failureHint?.explanation ?? '';
    }

    get errorCode(): string | null {
        return this.record.failureCause || null;
    }

    get targetAddress(): string | null {
        return this.record.terminatorRemote && this.record.terminatorRemote.length > 0 ? this.record.terminatorRemote : null;
    }

    // ---- Unified connection trace ------------------------------------------

    /**
     * Dialer → service → host as one top-down trace. Each hop carries the
     * routing state (where the dial broke) and the faithful entity name; the
     * failed hop gets the status sub-line.
     */
    get traceRows(): TraceRow[] {
        const states = this.hopStates();
        const target = this.targetAddress ?? '—';
        // Host sub-line only when the host was actually reached-and-failed.
        const hostSub = states.host === 'failed' ? `${states.hostNote}forwards to ${target}` : null;
        return [
            this.row('Dialer', 'dialer', this.record.identityName, states.dialer, null, this.identityLink(this.record.identityId)),
            this.row('Service', 'service', this.record.serviceName, states.service, null, this.serviceLink(this.record.serviceId)),
            this.transitRow(states.router),
            this.row('Host', 'host', this.record.hostName, states.host, hostSub, this.identityLink(this.record.hostId)),
        ];
    }

    // Records carry ziti ids that match the app routes (same as the list table).
    private identityLink(id: string | null | undefined): unknown[] | null {
        return id ? ['/identities', id] : null;
    }

    private serviceLink(id: string | null | undefined): unknown[] | null {
        return id ? ['/services/advanced', id, 'overview'] : null;
    }

    /** Navigating to an entity dismisses both this modal and the dial-logs drawer. */
    onNavigate(): void {
        this.dialogRef.close();
        this.drawerService.close();
    }

    /** Route state per hop + the host status note, derived from the failure side. */
    private hopStates(): {
        dialer: TraceRow['state'];
        service: TraceRow['state'];
        router: TraceRow['state'];
        host: TraceRow['state'];
        hostNote: string;
    } {
        switch (this.side) {
            case 'dialer':
                return { dialer: 'failed', service: 'unreached', router: 'unreached', host: 'unreached', hostNote: '' };
            case 'router':
                // The fabric couldn't route the circuit — it never reached a host.
                return { dialer: 'reached', service: 'reached', router: 'failed', host: 'unreached', hostNote: '' };
            case 'config':
                // No host to route to → nothing was actually routed, so the router is
                // neither a success (green) nor the culprit — show it neutral (idle).
                return { dialer: 'reached', service: 'reached', router: 'idle', host: 'failed', hostNote: 'no host serving · ' };
            case 'host':
                return { dialer: 'reached', service: 'reached', router: 'reached', host: 'failed', hostNote: 'no response · ' };
            default:
                return { dialer: 'reached', service: 'reached', router: 'reached', host: 'failed', hostNote: '' };
        }
    }

    /** The fabric/edge-router transit hop. No per-router identity is captured in the
     *  dial event yet, so it surfaces its routing status rather than a name. */
    private transitRow(state: TraceRow['state']): TraceRow {
        const base = {
            role: 'Edge Router' as const,
            state,
            key: 'router',
            raw: '',
            link: null,
            implicated: state === 'failed',
            sub: null,
            isTransit: true,
        };
        // Collapsed summary = fabric routers only (the terminating host node is its
        // own embedded router, shown as the HOST hop — not a fabric edge router).
        const allNodes = this.record.routerNodes ?? [];
        const hostId = this.record.hostId;
        const nodes = allNodes.filter((n) => n !== hostId);
        const map = this.data.routers;
        if (state !== 'unreached' && nodes.length > 0 && map) {
            const resolve = (ziti: string, role: string): { name: string; link: unknown[] | null; role: string } => {
                const r = map.get(ziti);
                // Managed edge-router detail route (matches the routers list's row link).
                return { name: r?.name ?? 'Edge router', link: r ? ['/network/edge-routers', r.id] : null, role };
            };
            const routers = nodes.map((ziti, i) =>
                resolve(ziti, i === 0 ? 'Ingress' : i === nodes.length - 1 ? 'Egress' : 'Transit')
            );
            const ends = routers.length <= 2 ? routers : [routers[0], routers[routers.length - 1]];
            // The expanded path is the full ordered circuit, ending at the host it
            // terminated on (labelled + linked as the host, not a router).
            const hostReached = !!hostId && allNodes.includes(hostId);
            const fullPath = hostReached
                ? [
                      ...routers,
                      { name: (this.record.hostName ?? '').trim() || 'Host', link: this.identityLink(hostId), role: 'Host' },
                  ]
                : routers;
            const links = this.record.linkCount ?? Math.max(allNodes.length - 1, 0);
            return {
                ...base,
                name: '',
                routers: ends.map((r) => ({ name: r.name, link: r.link })),
                routerMeta: this.routerMeta(nodes.length, links),
                hasMorePath: fullPath.length > ends.length,
                fullPath,
            };
        }
        // No recorded router path → fall back to a status that reflects why:
        // config failures never establish a circuit (no host to route to), so
        // "Circuit routed" would overstate what happened.
        const status =
            state === 'failed'
                ? 'No path to a host'
                : state === 'unreached'
                  ? 'Not reached'
                  : this.side === 'config'
                    ? 'No host to route to'
                    : 'Circuit routed';
        return { ...base, name: status, routers: undefined, routerMeta: null };
    }

    private routerMeta(routerCount: number, links: number): string {
        const r = `${routerCount} router${routerCount === 1 ? '' : 's'}`;
        return links > 0 ? `${r} · ${links} link${links === 1 ? '' : 's'}` : r;
    }

    private row(
        role: TraceRow['role'],
        key: string,
        raw: string | null | undefined,
        state: TraceRow['state'],
        sub: string | null,
        link: unknown[] | null
    ): TraceRow {
        // Show the entity name exactly as stored — nothing split off or assumed.
        const value = (raw ?? '').trim();
        return {
            role,
            state,
            key,
            name: value || '—',
            raw: value,
            // Only link a real name, never a placeholder dash.
            link: value ? link : null,
            implicated: state === 'failed',
            sub,
            isTransit: false,
        };
    }

    // ---- Controls ----------------------------------------------------------

    toggleFullPath(): void {
        this.showFullPath = !this.showFullPath;
    }

    copy(key: string, value: string): void {
        if (!value) return;
        navigator.clipboard?.writeText(value).then(() => {
            this.copiedKey = key;
            if (this.copyTimer) clearTimeout(this.copyTimer);
            this.copyTimer = setTimeout(() => (this.copiedKey = null), 1400);
        });
    }

    close(): void {
        this.dialogRef.close();
    }
}
